import { useEffect } from 'react';
import * as Sentry from '@sentry/react';
import { useLocation, useNavigate } from 'react-router-dom';
import axiosInstance from './api';
import {
  expiredAuthErrors,
  invalidGrantErrorMessages,
  ROUTES,
  STATUS_CODE,
} from '../constants/constants';
import { showToast } from '../components/utils/toast';
import { useTranslation } from 'react-i18next';
import { getEntityNotFoundMessage } from '../constants/utilityFunctions';
import { getForbiddenErrorType } from '../interface/errorTypes';
import { useAuthenticationProvider } from '../authentication/authProvider';

const AxiosInterceptor = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const location = useLocation();
  const { logout } = useAuthenticationProvider();

  useEffect(() => {
    const responseInterceptor = axiosInstance.interceptors.response.use(
      (response) => response,
      (error) => {
        const status = error.response?.status;
        const extra = {
          url: error.config?.url,
          method: error.config?.method,
          data: error.response?.data,
          status,
        };
        // the API reports a dead refresh token via a fixed message rather than a distinct status code
        const isInvalidGrantError = invalidGrantErrorMessages.includes(
          error?.response?.data?.message?.toLowerCase(),
        );

        const isAuthTokenExpired = expiredAuthErrors.includes(error?.error);
        const is500Error = status === STATUS_CODE.SOME_THING_WRONG;
        if (is500Error && !error?.config?.skipGlobalErrorHandler) {
          Sentry.captureException(error, { extra: { ...extra, route: location.pathname } });
        }
        const errorType = getForbiddenErrorType((error.response?.data as { type?: string })?.type);
        if (isInvalidGrantError) {
          // The refresh token itself is dead (not just the access token) - sign the user out instead
          // of routing them to the Access Forbidden interstitial, since retrying won't help.
          logout();
        } else if (
          status === STATUS_CODE.FORBIDDEN_EXCEPTION &&
          error?.config?.skipForbiddenRedirect
        ) {
          // The caller shows its own message for a forbidden request, so it needs the raw error
          return Promise.reject(error);
        } else if (status === STATUS_CODE.CONFLICT && error?.config?.handlesVersionConflict) {
          // The caller explains the version conflict itself and keeps the form so the user can save again
          return Promise.reject(error);
        } else if (
          status === STATUS_CODE.UNAUTHORIZED ||
          status === STATUS_CODE.FORBIDDEN_EXCEPTION ||
          isAuthTokenExpired
        ) {
          setTimeout(() => {
            navigate(ROUTES.ACCESS_FORBIDDEN, {
              state: {
                errorType,
              },
            });
          }, 0);
        } else if (status === STATUS_CODE.ENTITY_NOT_FOUND) {
          // NOTE: skipGlobalErrorHandler flag is true when we want to handle some specific component level handling
          if (error?.config?.skipGlobalErrorHandler) {
            return Promise.reject(error);
          }
          showToast('error', getEntityNotFoundMessage(error?.config?.headers?.entity, t));
          navigate(ROUTES.DASHBOARD);
        } else {
          // NOTE: skipGlobalErrorHandler flag is true when we want to handle some specific component level handling
          if (error?.config?.skipGlobalErrorHandler) {
            return Promise.reject(error);
          }
          setTimeout(() => {
            navigate(ROUTES.ERROR, {
              state: {
                status,
              },
            });
          }, 0);
        }
        return Promise.reject(new Error(error.response?.data?.message || 'Unknown error'));
      },
    );

    return () => {
      axiosInstance.interceptors.response.eject(responseInterceptor);
    };
  }, [navigate]);

  return null;
};

export default AxiosInterceptor;
